import { useEffect, useMemo, useState } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'
import { useSlsStateContext } from '@fs/zion-sls'
import { useIsOptedIn, setOptInOut } from '@fs/zion-soft-launch'
import { splunk } from '@fs/zion-splunk-logger'
import { useUser, useUserProfile } from '@fs/zion-user'
import { setPreference } from '@fs/zion-preferences'

// FSS-14667: the consolidated opt-in flag. This is the original V1 opt-in flow's flag - every opted-in
// user's choice is already stored under its soft-launch preference key (softLaunch.<flag>), so reusing
// it here preserves those settings directly, with no migration.
export const ATTACH_FLOW_FLAG = 'rip_sharedImageViewer_fullTextAttachFlow_v1'

// FSS-14667: master rollout kill-switch for the updated Names experience. When this is OFF the whole
// branch reverts to master behavior for everyone - the per-user opt-in below is ignored and toggle
// entry points are hidden. When ON, the per-user opt-in preference governs who sees it.
export const NAMES_EXPERIENCE_ROLLOUT_FLAG = 'rip_sharedImageViewer_namesExperienceRollout'

// FSS-14848: onboarding dialog seen-flags. A dialog may only open when NEITHER is set, so both must
// be read - checking just one would let the other fire on a later visit. Accepting the invite sets
// both (the invite marks itself seen, then turnOn suppresses the changelog), so treat them as a
// single "onboarding done" signal rather than assuming at most one is ever set.
export const NOT_TURNED_ON_SEEN_KEY = 'rip.fullTextAttachFlow.onboardingDialog.notTurnedOn.seen'
export const ALREADY_TURNED_ON_SEEN_KEY = 'rip.fullTextAttachFlow.onboardingDialog.alreadyTurnedOn.seen'

// Module-level store for the in-session opt-in override, so toggling gives instant feedback before
// the preference round-trips. null = no override.
let optInOverride = null
const overrideSubscribers = new Set()

function subscribeToOverride(callback) {
  overrideSubscribers.add(callback)
  return () => overrideSubscribers.delete(callback)
}

/**
 * Set (or clear with null) the in-session opt-in override.
 *
 * @param {boolean|null} value - true/false to override the read, null to defer to the flag again.
 * @returns {void}
 */
export function setOptInOverride(value) {
  optInOverride = value
  overrideSubscribers.forEach((callback) => callback())
}

/** @returns {boolean|null} the in-session opt-in override, or null when none is set. */
function useOptInOverride() {
  const [override, setOverride] = useState(optInOverride)

  useEffect(() => {
    // Re-sync in case the override changed between the initial render and this subscription.
    setOverride(optInOverride)
    return subscribeToOverride(() => setOverride(optInOverride))
  }, [])

  return override
}

/**
 * @typedef {Object} NamesExperienceAvailability
 * @property {boolean} available - Whether the updated Names experience is available at all: the
 *   master rollout flag is on AND there is a signed-in user to hold an opt-in preference.
 * @property {boolean} userLoading - True only while a pending signed-in state could still flip
 *   `available` to true. False whenever the answer is already definitive, so callers never block on
 *   a /user round-trip they don't need.
 */

/**
 * Whether the updated Names experience is available to the current visitor. The opt-in is a per-user
 * preference, so it is meaningless for signed-out visitors.
 *
 * @returns {NamesExperienceAvailability}
 */
function useNamesExperienceAvailability() {
  const rolloutFlag = useFeatureFlag(NAMES_EXPERIENCE_ROLLOUT_FLAG)
  const { signedIn, userLoading } = useUser()

  // With the flag off nothing can make the experience available, so don't report loading and make
  // callers wait on useUser - the kill-switch must stay a synchronous "master behavior" answer.
  // useUser starts every instance at { signedIn: false, userLoading: true } and only settles after
  // an async fetch, so treating that initial tick as "unavailable, done" would blank flag-on
  // consumers that render null while loading.
  if (!rolloutFlag.isOn) return { available: false, userLoading: false }

  return { available: !!signedIn, userLoading: !!userLoading }
}

/**
 * @typedef {Object} NamesExperienceOptIn
 * @property {boolean} available - Whether the experience is available to this visitor at all (rollout
 *   flag on and signed in). Exposed so callers needing both this and the opt-in share one useUser().
 * @property {boolean} isOptedIn - true when the updated Names experience should render for this user.
 * @property {boolean} loading - true while the opt-in preference is still resolving; defer swapping
 *   layouts until this is false to avoid a flash of the wrong experience.
 */

/**
 * Whether the current user should see the updated Names experience, from the consolidated opt-in
 * preference with an in-session override for instant toggle feedback. Pure read.
 *
 * @returns {NamesExperienceOptIn}
 */
export default function useNamesExperienceOptIn() {
  const { available, userLoading } = useNamesExperienceAvailability()
  const { isOptedIn, loading } = useIsOptedIn({ featureFlag: ATTACH_FLOW_FLAG })
  const override = useOptInOverride()

  // Kill-switch: unavailable means everyone gets master behavior, so the opt-in and override are
  // moot. Report loading while the signed-in state resolves, so a signed-in user doesn't flash
  // master behavior before their opt-in applies.
  if (!available) return { available, isOptedIn: false, loading: userLoading }

  if (override !== null) return { available, isOptedIn: override, loading: false }
  return { available, isOptedIn, loading }
}

/**
 * @typedef {Object} NamesExperienceToggle
 * @property {boolean} isOptedIn - Whether the updated Names experience is currently on for this user.
 * @property {boolean} loading - True while the opt-in preference is still resolving
 * @property {boolean} rolloutEnabled - Whether the updated experience is available to this visitor:
 *   the master rollout flag is on and they are signed in. When false, the experience is unavailable;
 *   entry points that invite the user should hide themselves.
 * @property {() => void} turnOn - Opt the user into the updated Names experience.
 * @property {(options?: {reason?: string, mayContact?: boolean}) => void} turnOff - Opt the user out.
 *   Logs an optional turn-off reason to splunk, carrying the user's email only when they consented
 *   to being contacted via `mayContact`.
 */

/**
 * Toggle for the updated Names experience: the current state plus turn-on/off writes (turn-off logs
 * the reason to splunk), so every entry point shares one contract.
 *
 * Must be called within a SlsContextProvider - the turn-off log carries the current image ark.
 *
 * @returns {NamesExperienceToggle}
 */
export function useNamesExperienceToggle() {
  // Every rendered value comes from this one call. useUser keeps per-instance state, so resolving
  // availability again here would mount a second, independently settling user state machine - and
  // rolloutEnabled could then disagree with loading in a commit.
  const { available: rolloutEnabled, isOptedIn, loading } = useNamesExperienceOptIn()
  // Read straight from useUser to keep cisId out of the hooks above, whose other consumers don't
  // want it. Safe only because nothing renders off it - don't widen this to signedIn/userLoading.
  const { cisId } = useUser()
  const { currentImageArk } = useSlsStateContext()

  // Contact details come from the profile service, not useUser - /platform/users/current only
  // carries an email once the user has added one to their profile. The address is the NOTIFICATIONS
  // one, which the service marks PRIVATE; the consent box in the turn-off dialog is what permits
  // logging it, so it is only ever sent when the user ticks that box. The name has no equivalent
  // consent, so it is logged only while the user keeps it public.
  const { userProfile } = useUserProfile()
  const notificationsEmail =
    userProfile?.contactInfo?.methods?.find(
      (method) => method.method === 'EMAIL' && method.preferredUsages?.includes('NOTIFICATIONS')
    )?.value ?? null
  const name = userProfile?.name?.visibility === 'PUBLIC' ? userProfile.name.value : null

  return useMemo(() => {
    const setIsOptedIn = (newPref) => {
      // The persisted write is the source of truth once the in-session override is lost (e.g. on a
      // full page reload). It is fire-and-forget, so a failure - a degraded preferences service, or
      // no cisId to attach the preference to - is otherwise invisible: the override already swapped
      // the UI, so it looks like the choice stuck when it did not. Log failures with the image ark
      // and cis id (matching the turn-off log) so FSS-15001 can be confirmed from Splunk.
      setOptInOut({ newPref, featureFlag: ATTACH_FLOW_FLAG, reloadPage: false }).catch((error) => {
        try {
          splunk.log({
            message: 'Updated look preference failed to save',
            newPref,
            imageArk: currentImageArk,
            cisId,
            error: error?.message,
          })
        } catch {
          // Best-effort logging; a logging failure must never surface to the user.
        }
      })
      // Override the read immediately so the UI swaps experiences before the preference round-trips.
      setOptInOverride(newPref)
    }

    return {
      isOptedIn,
      loading,
      rolloutEnabled,
      // Guard the write: when the experience is unavailable (flag off, or signed out with nowhere to
      // store a per-user preference) opting in would only leave a preference the kill-switch ignores.
      turnOn: () => {
        if (!rolloutEnabled) return
        setIsOptedIn(true)
        // The changelog exists to tell already-opted-in users what changed, so opting in suppresses
        // it rather than showing it next visit. Fire-and-forget: never block the opt-in.
        setPreference(ALREADY_TURNED_ON_SEEN_KEY, true).catch((error) => {
          console.error('[useNamesExperienceToggle] Failed to suppress the changelog dialog:', error)
          // A lost seen-flag re-shows an onboarding dialog on the next visit, so log it (FSS-15001).
          try {
            splunk.log({
              message: 'Updated look onboarding seen-flag failed to save',
              seenKey: ALREADY_TURNED_ON_SEEN_KEY,
              imageArk: currentImageArk,
              cisId,
              error: error?.message,
            })
          } catch {
            // Best-effort logging.
          }
        })
      },
      turnOff: ({ reason, mayContact = false } = {}) => {
        // Write the opt-out state first: a logging failure must never block the actual opt-out.
        setIsOptedIn(false)
        const trimmedReason = reason?.trim()
        if (trimmedReason) {
          try {
            splunk.log({
              message: 'Updated look turned off',
              reason: trimmedReason,
              imageArk: currentImageArk,
              cisId,
              // Deliberately keeps the field name the released log already uses so existing splunk
              // queries keep resolving. It now carries the NOTIFICATIONS address, not the
              // contact-card one, and only when the user consented.
              contactCardEmail: mayContact ? notificationsEmail : null,
              mayContact,
              name,
            })
          } catch {
            // Best-effort logging; swallow so the opt-out still succeeds.
          }
        }
      },
    }
  }, [isOptedIn, loading, rolloutEnabled, currentImageArk, cisId, notificationsEmail, name])
}
